import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import NoPermission from "components/common/NoPermission";
import PageHeader from "components/common/PageHeader";
import Paginations from "components/common/Paginations";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { showDeleteModal } from "store/slices/modalSlice";
import {
  getNotificationRules,
  resetFilters,
  setFilter,
  updateNotificationRuleStatus,
} from "store/slices/notificationRulesSlice";
import { AUDIENCE_TYPES, STATUS_OPTIONS } from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { getMinMaxDate, getTimeAgo } from "utils/utils";

const Rules = () => {
  const { t } = useTranslation();
  const { view, trash, edit, noPlans } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("notifications");

  const {
    loading,
    isLoading,
    totalPages,
    currentPage,
    totalCount,
    items,
    loadingItemId,
    filters: { perPage, pageNo, orderBy, status, keyword, dates },
  } = useSelector((state) => state.NotificationRules);

  useEffect(() => {
    canView && getAllData();
  }, [canView]);

  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage,
      pageNo: page || pageNo,
      orderBy: isReset ? -1 : orderBy,
      status: isReset ? -1 : status,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
    };
    dispatch(getNotificationRules(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const OPERATOR_LABEL_MAP = {
    EQUALS: "=",
    NOT_EQUALS: "≠",
    GT: ">",
    GTE: "≥",
    LT: "<",
    LTE: "≤",
    CONTAINS: "notificationRule.operator.contains",
    EXISTS: "notificationRule.operator.exists",
  };

  const EVENT_LABEL_MAP = {
    NO_WASH_SINCE: "notificationRule.event.noWashSince",
    ACCOUNT_CREATED: "notificationRule.event.accountCreated",
    BIRTHDAY: "notificationRule.event.birthday",
    MEMBERSHIP_PURCHASED: "notificationRule.event.membershipPurchased",
    MEMBERSHIP_EXPIRY: "notificationRule.event.membershipExpiry",
  };

  const formatUnit = (unit, value) => {
    if (!unit) return "";

    const map = {
      MINUTES: "minute",
      HOURS: "hour",
      DAYS: "day",
    };

    const pluralKey = unit === "DAYS" ? "daysPlural" : `${map[unit]}s`;
    const label = map[unit]
      ? t(`notificationRule.unit.${value > 1 ? pluralKey : map[unit]}`)
      : unit.toLowerCase();
    return label;
  };

  const formatCondition = (condition) => {
    const eventKey = EVENT_LABEL_MAP[condition.event];
    const eventLabel = eventKey ? t(eventKey) : condition.event;

    const operatorKey = OPERATOR_LABEL_MAP[condition.operator];
    const operatorLabel =
      operatorKey?.includes(".") ? t(operatorKey) : operatorKey || condition.operator;

    // EXISTS operator has no value/unit
    if (condition.operator === "EXISTS") {
      return `${eventLabel} ${t("notificationRule.operator.exists")}`;
    }

    return `${eventLabel} ${operatorLabel} ${condition.value} ${formatUnit(
      condition.unit,
      condition.value
    )}`;
  };

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: STATUS_OPTIONS.map((option) => ({
          ...option,
          name: t(option.name),
        })),
      },
    ],
    [t]
  );

  const columns = useMemo(
    () => [
      {
        header: t("Rule Name"),
        key: "name",
        render: (value, item) => (
          <div>
            <div className="fw-semibold">{value}</div>
            {item?.execution?.lastRunAt && (
              <div className="sub_text mt-1 small">
                {t("Last sent")}: {getTimeAgo(item?.execution?.lastRunAt)}
              </div>
            )}
          </div>
        ),
      },
      {
        header: t("Trigger Condition"),
        key: "trigger",
        render: (trigger) => (
          <div>
            {trigger?.conditions?.map((condition, index) => (
              <div key={condition._id || index}>
                {formatCondition(condition)}
              </div>
            ))}
          </div>
        ),
      },
      {
        header: t("Target Audience"),
        key: "audience",
        render: (value) =>
          AUDIENCE_TYPES?.find((option) => option.value === value?.type)
            ?.name
            ? t(
                AUDIENCE_TYPES.find((option) => option.value === value?.type)
                  .name,
              )
            : "-",
      },
      {
        header: t("Status"),
        key: "status",
        render: (value) => <StatusBadge status={value} t={t} />,
      },
    ],
    [t]
  );

  const renderActions = useCallback(
    (item) => (
      <>
        {canEdit && (
          <>
            <Switch
              checked={item?.status === 1}
              handleOnChange={() => {
                handleStatusUpdate({
                  _id: item._id,
                  status: item?.status,
                  dispatch,
                  action: updateNotificationRuleStatus,
                });
              }}
              disabled={isLoading && loadingItemId === item._id}
            />
            <img
              src={edit}
              width={20}
              height={20}
              alt="edit"
              onClick={() =>
                navigate(`/notification/rules/edit-rule?id=${item._id}`)
              }
            />
          </>
        )}
        {/* <Link to={`/notification/history/history-details?id=${item._id}`}>
          <img src={view} width={20} height={20} alt="view" />
        </Link> */}
        {canDelete && (
          <img
            src={trash}
            width={20}
            height={20}
            alt="delete"
            onClick={() => {
              dispatch(
                showDeleteModal({
                  title: t("Delete Notification Rule"),
                  message: t(
                    "Are you sure you want to delete this Notification Rule?"
                  ),
                  itemId: item._id,
                  actionType: "NOTIFICATION_RULE",
                })
              );
            }}
          />
        )}
      </>
    ),
    []
  );

  if (!canView) return <NoPermission />;

  return (
    <div className="page-content">
      <PageHeader title={t("Notification Rules")}/>

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search by Title")}
        filters={filtersList}
        selectedFilters={{ keyword, status }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add Rule")}
        onBtnClick={() => navigate("/notification/rules/add-rule")}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No notification rules found")} icon={noPlans} />
      )}
      {!loading && items?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={items}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default Rules;
